import React, { useState } from 'react';
import { useAgent } from '../../context/AgentContext';
import { Bot, FileText, MessageSquare, Zap, Shield, BarChart3, ArrowRight } from 'lucide-react';
import CreateAgentModal from '../agents/CreateAgentModal';
import './WelcomeScreen.css';

const features = [
  {
    icon: FileText,
    title: 'Document Intelligence',
    description: 'Upload PDFs, Word docs, and text files to create your knowledge base'
  },
  {
    icon: MessageSquare,
    title: 'Smart Conversations',
    description: 'Chat with your documents using natural language questions'
  },
  {
    icon: Zap,
    title: 'Instant Insights',
    description: 'Extract action items, summaries, and key information automatically'
  },
  {
    icon: Shield,
    title: 'Risk Analysis',
    description: 'Identify compliance issues and potential risks in your documents'
  },
  {
    icon: BarChart3,
    title: 'Generate Reports',
    description: 'Create professional PDF reports from your conversations'
  },
  {
    icon: Bot,
    title: 'Specialized Agents',
    description: 'Create Finance, HR, Legal, or custom AI agents for different needs'
  }
];

const WelcomeScreen = () => {
  const { agents } = useAgent();
  const [showCreateModal, setShowCreateModal] = useState(false);

  return (
    <div className="welcome-screen">
      <div className="welcome-content">
        <div className="welcome-header">
          <div className="welcome-icon">
            <Bot size={40} />
          </div>
          <h1>Welcome to Enterprise AI Platform</h1>
          <p>
            Transform your documents into intelligent insights. Create AI agents
            that understand your business documents and provide instant answers.
          </p>
        </div>

        {agents.length === 0 ? (
          <div className="welcome-cta">
            <button
              className="btn btn-primary btn-lg"
              onClick={() => setShowCreateModal(true)}
            >
              <Bot size={20} />
              Create Your First Agent
              <ArrowRight size={20} />
            </button>
            <p className="cta-hint">Get started in seconds</p>
          </div>
        ) : (
          <div className="welcome-cta">
            <p className="cta-select">Select an agent from the sidebar to get started</p>
          </div>
        )}

        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">
                <feature.icon size={24} />
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>

        <div className="welcome-footer">
          <div className="how-it-works">
            <h2>How It Works</h2>
            <div className="steps">
              <div className="step">
                <span className="step-number">1</span>
                <div className="step-content">
                  <h4>Create an Agent</h4>
                  <p>Choose a specialized agent type (Finance, HR, Legal) or create a custom one</p>
                </div>
              </div>
              <div className="step">
                <span className="step-number">2</span>
                <div className="step-content">
                  <h4>Upload Documents</h4>
                  <p>Add your PDFs, Word docs, or text files to build the agent's knowledge base</p>
                </div>
              </div>
              <div className="step">
                <span className="step-number">3</span>
                <div className="step-content">
                  <h4>Start Asking</h4>
                  <p>Ask questions, generate summaries, extract action items, and more</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateAgentModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  );
};

export default WelcomeScreen;
